import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import BreakCoverageEditor from '@/components/BreakCoverageEditor';
import SpotPhotosGallery from '@/components/SpotPhotosGallery';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { MapPin, Plus, Trash2, UserPlus, UserMinus, Sparkles, Upload, Search, Grid, List, Filter, X } from 'lucide-react';
import SpotImport from './SpotImport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpotManagement({
  eventId,
  eventType = 'general',
  customModalities = [],
  spots,
  onSpotsChange,
  photographers,
  eventPhotographers,
  setMapMode,
  onSpotSelect,
  courseTimings = [],
  editSpotIdRequest = null,
  onEditConsumed,
}) {
  const breakCovPhotographers = eventPhotographers || photographers || [];
  const [open, setOpen] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [spotForm, setSpotForm] = useState({
    name: '',
    lat: null,
    lng: null,
    address: '',
    description: '',
    coverage_role: 'Wide',
    modalities: [],
    required_gear: [],
    has_flash: false,
    use_led_flash: false,
    break_time_1: '',
    break_time_2: '',
    arrive_time: '',
    leave_time: '',
    first_athlete_time: '',
    last_athlete_time: '',
    cutoff_time: '',
    notes: '',
    max_photographers: 1,
    priority: 'medium',
    reference_images: []
  });
  const [gearInput, setGearInput] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSpotForAssign, setSelectedSpotForAssign] = useState(null);
  const [customModality, setCustomModality] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // Image Library Selector States
  const [showLibrarySelector, setShowLibrarySelector] = useState(false);
  const [libraryImages, setLibraryImages] = useState([]);
  const [libraryPacks, setLibraryPacks] = useState([]);
  const [selectedLibraryPack, setSelectedLibraryPack] = useState('All');
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [selectedLibraryImages, setSelectedLibraryImages] = useState([]);
  
  // AI Assignment Preview States
  const [showAIPreview, setShowAIPreview] = useState(false);
  const [aiAssignments, setAiAssignments] = useState([]);
  const [aiSchedules, setAiSchedules] = useState({});
  const [aiWarnings, setAiWarnings] = useState([]);
  const [aiUnassigned, setAiUnassigned] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiLoadingStatus, setAiLoadingStatus] = useState('');
  
  // Search and Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [modalityFilter, setModalityFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // all, assigned, unassigned
  const [viewMode, setViewMode] = useState('list'); // list or grid
  const [showFilters, setShowFilters] = useState(false);


  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const coverageRoles = ['Wide', 'Tele', 'Crowd', 'Drone', 'Social', 'Video'];
  const priorities = ['high', 'medium', 'low'];
  
  // All available presets
  const allPresets = {
    sports: { 'Sports': ['Run', 'Swimming', 'Bike', 'Rowing', 'Finish Line', 'Start Line', 'Transition Zone', 'Aid Station'] },
    fitness: { 'Fitness/Hyrox': ['SKIERG', 'SLED PUSH', 'SLED PULL', 'BURPEE BROAD JUMPS', 'ROWING', 'FARMERS CARRY', 'LUNGES', 'WALL BALLS'] },
    events: { 'Events': ['Ceremony', 'Reception', 'Stage', 'Backstage', 'VIP Area', 'Entrance', 'Red Carpet'] },
    wedding: { 'Wedding': ['Ceremony', 'First Dance', 'Reception', 'Getting Ready', 'Portraits', 'Group Photos', 'Details', 'Cake Cutting'] },
    real_estate: { 'Real Estate': ['Exterior', 'Interior', 'Kitchen', 'Living Room', 'Bedroom', 'Bathroom', 'Aerial/Drone', 'Twilight'] },
    corporate: { 'Corporate': ['Headshots', 'Product', 'Office', 'Meeting Room', 'Event Space', 'Branding'] },
    general: { 'Common': ['Wide Shot', 'Detail', 'Portrait', 'Group', 'Action', 'Candid', 'Establishing Shot'] },
  };

  // Show only modalities for this event type
  const modalityGroups = (() => {
    const groups = allPresets[eventType] || allPresets.general;
    if (customModalities.length > 0) {
      return { ...groups, 'Custom': customModalities };
    }
    return groups;
  })();
  
  // Get all unique modalities from spots
  const allModalities = [...new Set(spots.flatMap(s => s.modalities || []))];
  
  // Filter spots based on search and filters
  const filteredSpots = spots.filter(spot => {
    // Search filter
    const matchesSearch = !searchTerm || 
      spot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Modality filter
    const matchesModality = modalityFilter === 'all' || 
      spot.modalities?.includes(modalityFilter);
    
    // Role filter
    const matchesRole = roleFilter === 'all' || 
      spot.coverage_role === roleFilter;
    
    // Assignment filter
    const hasAssignments = spot.assigned_photographers && spot.assigned_photographers.length > 0;
    const matchesAssignment = 
      assignmentFilter === 'all' ||
      (assignmentFilter === 'assigned' && hasAssignments) ||
      (assignmentFilter === 'unassigned' && !hasAssignments);
    
    return matchesSearch && matchesModality && matchesRole && matchesAssignment;
  });

  // Common lenses/gear by sport
  const commonGearBySport = {
    'Run': ['70-200mm f/2.8', '24-70mm f/2.8', 'Wide angle 16-35mm', 'Telephoto 300mm+', 'Flash/Strobe'],
    'Swimming': ['70-200mm f/2.8', 'Telephoto 300mm+', 'Underwater housing', 'Waterproof camera', 'Flash/Strobe'],
    'Bike': ['70-200mm f/2.8', 'Telephoto 300mm+', '24-70mm f/2.8', 'Drone', 'Remote camera'],
    'SKIERG': ['24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'SLED PUSH': ['24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'SLED PULL': ['24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'BURPEE BROAD JUMPS': ['24-70mm f/2.8', 'Wide angle 16-35mm', 'Flash/Strobe', '70-200mm f/2.8'],
    'ROWING': ['70-200mm f/2.8', '24-70mm f/2.8', 'Flash/Strobe', 'Telephoto 300mm+'],
    'FARMERS CARRY': ['24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe'],
    'LUNGES': ['24-70mm f/2.8', 'Wide angle 16-35mm', 'Flash/Strobe'],
    'WALL BALLS': ['24-70mm f/2.8', '70-200mm f/2.8', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'General': ['24-70mm f/2.8', '70-200mm f/2.8', 'Wide angle 16-35mm', 'Telephoto 300mm+', 'Flash/Strobe', 'Drone']
  };

  // Get suggested gear
  const getSuggestedGear = () => {
    const allSuggestions = new Set();
    spotForm.modalities.forEach(modality => {
      const gearList = commonGearBySport[modality] || commonGearBySport['General'];
      gearList.forEach(gear => allSuggestions.add(gear));
    });
    return Array.from(allSuggestions);
  };

  // Get timing suggestions
  const getTimingSuggestions = () => {
    const suggestions = [];
    
    // Handle new structure: courseTimings is single object with modality_timings array
    if (courseTimings && courseTimings.modality_timings) {
      courseTimings.modality_timings.forEach(modalityTiming => {
        if (spotForm.modalities.includes(modalityTiming.modality)) {
          // Add suggestion for this modality with available times
          const timeParts = [];
          if (modalityTiming.start_time) timeParts.push(`Start: ${modalityTiming.start_time}`);
          if (modalityTiming.first_finisher_time) timeParts.push(`First Finisher: ${modalityTiming.first_finisher_time}`);
          if (modalityTiming.last_finisher_time) timeParts.push(`Last Finisher: ${modalityTiming.last_finisher_time}`);
          if (modalityTiming.cutoff_time) timeParts.push(`Cutoff: ${modalityTiming.cutoff_time}`);
          
          if (timeParts.length > 0) {
            suggestions.push({
              modality: modalityTiming.modality,
              timeInfo: timeParts.join(' | '),
              data: modalityTiming
            });
          }
        }
      });
    }
    
    return suggestions;
  };

  const handleCreateSpot = () => {
    setEditingSpot(null);
    setSpotForm({
      name: '',
      lat: null,
      lng: null,
      address: '',
      description: '',
      coverage_role: 'Wide',
      modalities: [],
      required_gear: [],
      has_flash: false,
      use_led_flash: false,
      break_time_1: '',
      break_time_2: '',
      arrive_time: '',
      leave_time: '',
      first_athlete_time: '',
      last_athlete_time: '',
      cutoff_time: '',
      notes: '',
      max_photographers: 1,
      priority: 'medium',
      reference_images: []
    });
    setOpen(true);
    setMapMode('create_spot');
    toast.info('Click on the map to place the spot');
  };
  
  // Update spot location when creating
  useEffect(() => {
    if (open && !editingSpot && spotForm.lat !== null) {
      // Location was set, can now save
    }
  }, [spotForm.lat, spotForm.lng]);

  // External request to open the edit dialog for a specific spot.
  useEffect(() => {
    if (!editSpotIdRequest) return;
    const spot = (spots || []).find((s) => s.id === editSpotIdRequest);
    if (spot) {
      handleEditSpot(spot);
    }
    onEditConsumed?.();
  }, [editSpotIdRequest]); // eslint-disable-line

  const handleEditSpot = (spot) => {
    setEditingSpot(spot);
    setSpotForm({
      name: spot.name,
      lat: spot.lat,
      lng: spot.lng,
      address: spot.address || '',
      description: spot.description || '',
      coverage_role: spot.coverage_role,
      modalities: spot.modalities || [],
      required_gear: spot.required_gear || [],
      has_flash: spot.has_flash || false,
      use_led_flash: spot.use_led_flash || false,
      break_time_1: spot.break_time_1 || '',
      break_time_2: spot.break_time_2 || '',
      arrive_time: spot.arrive_time || '',
      leave_time: spot.leave_time || '',
      first_athlete_time: spot.first_athlete_time || '',
      last_athlete_time: spot.last_athlete_time || '',
      cutoff_time: spot.cutoff_time || '',
      notes: spot.notes || '',
      max_photographers: spot.max_photographers || 1,
      priority: spot.priority || 'medium',
      reference_images: spot.reference_images || []
    });
    setOpen(true);
  };
  
  // Expose method to set spot location from map click
  useEffect(() => {
    if (setMapMode) {
      window.setSpotLocation = (lat, lng) => {
        setSpotForm(prev => ({ ...prev, lat, lng }));
        toast.success('Spot location set');
        setMapMode('normal');
      };
    }
    return () => {
      delete window.setSpotLocation;
    };
  }, [setMapMode]);


  // Fetch library images when selector opens
  useEffect(() => {
    if (showLibrarySelector) {
      fetchLibraryImages();
      setSelectedLibraryImages([]);
    }
  }, [showLibrarySelector]);

  const fetchLibraryImages = async () => {
    try {
      const [imagesRes, packsRes] = await Promise.all([
        axios.get(`${API}/image-library`, config),
        axios.get(`${API}/image-packs`, config)
      ]);
      setLibraryImages(imagesRes.data);
      setLibraryPacks(packsRes.data);
    } catch (error) {
      console.error('Error fetching library images:', error);
      toast.error('Failed to load image library');
    }
  };

  const handleAddLibraryImages = async () => {
    if (selectedLibraryImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    try {
      // Get the image URLs from selected images
      const selectedUrls = libraryImages
        .filter(img => selectedLibraryImages.includes(img.id))
        .map(img => img.image_url);

      // Add to spotForm's reference_images
      const updatedImages = [...(spotForm.reference_images || []), ...selectedUrls];
      setSpotForm({ ...spotForm, reference_images: updatedImages });
      
      setSelectedLibraryImages([]);
      setShowLibrarySelector(false);
      toast.success(`✅ Added ${selectedLibraryImages.length} images from library`);
    } catch (error) {
      console.error('Error adding library images:', error);
      toast.error('Failed to add images');
    }
  };

  const toggleLibraryImageSelection = (imageId) => {
    setSelectedLibraryImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const handleSaveSpot = async () => {
    try {
      if (editingSpot) {
        // Update existing spot
        await axios.put(`${API}/spots/${editingSpot.id}`, spotForm, config);
        toast.success('Spot updated successfully');
      } else {
        // Create new spot
        await axios.post(`${API}/spots`, { ...spotForm, event_id: eventId }, config);
        toast.success('Spot created successfully');
      }
      setOpen(false);
      onSpotsChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save spot');
    }
  };

  const handleDeleteSpot = async (spotId) => {
    if (!window.confirm('Are you sure you want to delete this spot?')) return;

    try {
      await axios.delete(`${API}/spots/${spotId}`, config);
      toast.success('Spot deleted successfully');
      onSpotsChange();
    } catch (error) {
      toast.error('Failed to delete spot');
    }
  };

  const handleAddGear = () => {
    if (gearInput.trim()) {
      setSpotForm(prev => ({
        ...prev,
        required_gear: [...prev.required_gear, gearInput.trim()]
      }));
      setGearInput('');
    }
  };

  const handleRemoveGear = (index) => {
    setSpotForm(prev => ({
      ...prev,
      required_gear: prev.required_gear.filter((_, i) => i !== index)
    }));
  };

  const toggleModality = (modality) => {
    setSpotForm(prev => {
      const newModalities = prev.modalities.includes(modality)
        ? prev.modalities.filter(m => m !== modality)
        : [...prev.modalities, modality];
      
      // Auto-set name to first selected modality if name is empty or matches old modality
      let newName = prev.name;
      if (newModalities.length > 0 && (!prev.name || prev.modalities.includes(prev.name))) {
        newName = newModalities[0];
      } else if (newModalities.length === 0) {
        newName = '';
      }
      
      // Auto-populate arrive/leave times when adding a new modality
      let arriveTime = prev.arrive_time;
      let leaveTime = prev.leave_time;
      
      if (newModalities.includes(modality) && !prev.modalities.includes(modality)) {
        if (courseTimings && courseTimings.modality_timings) {
          const modalityTiming = courseTimings.modality_timings.find(mt => mt.modality === modality);
          if (modalityTiming) {
            // Arrive time = 1 hour before start_time
            if (modalityTiming.start_time) {
              const [hours, minutes] = modalityTiming.start_time.split(':').map(Number);
              const newHours = hours - 1;
              if (newHours >= 0) {
                arriveTime = `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
              }
            }
            // Leave time = cutoff_time
            if (modalityTiming.cutoff_time) {
              leaveTime = modalityTiming.cutoff_time;
            }
          }
        }
      }
      
      return {
        ...prev,
        modalities: newModalities,
        name: newName,
        arrive_time: arriveTime,
        leave_time: leaveTime
      };
    });
  };

  const addCustomModality = () => {
    if (customModality.trim() && !spotForm.modalities.includes(customModality.trim())) {
      setSpotForm(prev => {
        const newModalities = [...prev.modalities, customModality.trim()];
        // Auto-set name if empty
        const newName = !prev.name ? customModality.trim() : prev.name;
        return {
          ...prev,
          modalities: newModalities,
          name: newName
        };
      });
      setCustomModality('');
    }
  };

  const removeModality = (modality) => {
    setSpotForm(prev => ({
      ...prev,
      modalities: prev.modalities.filter(m => m !== modality)
    }));
  };

  const handleAssignPhotographer = async (spotId, photographerId) => {
    try {
      await axios.post(`${API}/spots/${spotId}/assign`, {
        spot_id: spotId,
        photographer_id: photographerId
      }, config);
      toast.success('Photographer assigned successfully');
      onSpotsChange();
      setAssignDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign photographer');
    }
  };

  const handleUnassignPhotographer = async (spotId, photographerId) => {
    try {
      await axios.delete(`${API}/spots/${spotId}/unassign/${photographerId}`, config);
      toast.success('Photographer unassigned successfully');
      onSpotsChange();
    } catch (error) {
      toast.error('Failed to unassign photographer');
    }
  };

  const handleAIAssign = async () => {
    setLoadingAI(true);
    setAiLoadingStatus('🤖 Initializing AI analysis...');
    
    // Set a timeout warning after 30 seconds
    const timeoutWarning = setTimeout(() => {
      setAiLoadingStatus('⚠️ Taking longer than expected... Please wait...');
    }, 30000);
    
    try {
      // Simulate status updates
      setTimeout(() => setAiLoadingStatus('📊 Analyzing photographer skills and gear...'), 500);
      setTimeout(() => setAiLoadingStatus('📍 Processing spot requirements and locations...'), 1500);
      setTimeout(() => setAiLoadingStatus('⏰ Calculating travel times and schedules...'), 3000);
      setTimeout(() => setAiLoadingStatus('🧠 AI is generating optimal assignments...'), 5000);
      
      const response = await axios.post(`${API}/spots/ai-assign/${eventId}`, {}, config);
      
      clearTimeout(timeoutWarning);
      setAiLoadingStatus('✅ Analysis complete! Preparing preview...');
      
      // Store AI results for preview
      setAiAssignments(response.data.assignments || []);
      setAiSchedules(response.data.photographer_schedules || {});
      setAiWarnings(response.data.warnings || []);
      setAiUnassigned(response.data.unassigned_spots || []);
      
      // Show preview dialog
      setTimeout(() => {
        setShowAIPreview(true);
        setLoadingAI(false);
        setAiLoadingStatus('');
        toast.success('AI analysis complete! Review assignments below.');
      }, 500);
      
    } catch (error) {
      clearTimeout(timeoutWarning);
      setLoadingAI(false);
      setAiLoadingStatus('');
      toast.error(error.response?.data?.detail || 'AI assignment failed');
    }
  };

  const handleApplyAIAssignments = async () => {
    try {
      const response = await axios.post(
        `${API}/spots/ai-assign/${eventId}/apply`,
        aiAssignments,
        config
      );
      
      toast.success(`✅ Applied ${response.data.applied_count} assignments!`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(error => toast.error(error));
      }
      
      setShowAIPreview(false);
      onSpotsChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply assignments');
    }
  };

  const handleEditAssignment = (index, field, value) => {
    const updated = [...aiAssignments];
    updated[index][field] = value;
    setAiAssignments(updated);
  };

  const handleRemoveAssignment = (index) => {
    const updated = aiAssignments.filter((_, i) => i !== index);
    setAiAssignments(updated);
  };


  const getPhotographerById = (id) => {
    return photographers.find(p => p.id === id);
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex justify-between items-center flex-shrink-0">
        <h3 className="text-lg font-semibold">Photography Spots</h3>
        <div className="flex gap-2">
          <Button onClick={() => setImportDialogOpen(true)} variant="outline" size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Import Spots
          </Button>
          <Button onClick={handleAIAssign} variant="outline" size="sm" disabled={loadingAI}>
            <Sparkles className="h-4 w-4 mr-2" />
            {loadingAI ? 'Analyzing...' : 'AI Assign'}
          </Button>
          <Button onClick={handleCreateSpot} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Create Spot
          </Button>
        </div>
      </div>

      {/* Search, Filter, and View Toggle Bar */}
      <div className="space-y-3 flex-shrink-0">
        {/* Search and View Toggle Row */}
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search spots by name, address, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-emerald-50 border-emerald-500' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          
          <div className="flex gap-1 border rounded-lg p-1">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="h-8 w-8 p-0"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="h-8 w-8 p-0"
            >
              <Grid className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filter Bar (Collapsible) */}
        {showFilters && (
          <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg border">
            <div>
              <Label className="text-xs text-gray-600 mb-1">Modality</Label>
              <Select value={modalityFilter} onValueChange={setModalityFilter}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modalities</SelectItem>
                  {allModalities.map(mod => (
                    <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-600 mb-1">Coverage Role</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {coverageRoles.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-600 mb-1">Assignment Status</Label>
              <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Spots</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(modalityFilter !== 'all' || roleFilter !== 'all' || assignmentFilter !== 'all') && (
              <div className="col-span-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setModalityFilter('all');
                    setRoleFilter('all');
                    setAssignmentFilter('all');
                  }}
                  className="text-xs"
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* Results Count */}
        <div className="text-sm text-gray-600">
          Showing {filteredSpots.length} of {spots.length} spots
        </div>
      </div>

      {/* Spots List or Grid */}
      <div className="flex-1 overflow-y-auto">
        {filteredSpots.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchTerm || modalityFilter !== 'all' || roleFilter !== 'all' || assignmentFilter !== 'all' ? (
              <>
                <p className="text-lg mb-2">No spots match your filters</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setModalityFilter('all');
                    setRoleFilter('all');
                    setAssignmentFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              </>
            ) : (
              <p>No spots created yet</p>
            )}
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="space-y-2">
            {filteredSpots.map(spot => (
          <div key={spot.id} className="border rounded-lg p-3 bg-white/50 backdrop-blur-sm">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">{spot.name}</span>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                    {spot.coverage_role}
                  </span>
                  {spot.has_flash && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                      Flash
                    </span>
                  )}
                </div>
                {spot.address && (
                  <p className="text-sm text-gray-600 mt-1">📍 {spot.address}</p>
                )}
                {spot.description && (
                  <p className="text-sm text-gray-600 mt-1">{spot.description}</p>
                )}
                {spot.modalities && spot.modalities.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="text-xs text-gray-500">Activities:</span>
                    {spot.modalities.map((modality, idx) => (
                      <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        {modality}
                      </span>
                    ))}
                  </div>
                )}
                {(spot.arrive_time || spot.leave_time) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {spot.arrive_time && `Arrive: ${spot.arrive_time}`}
                    {spot.arrive_time && spot.leave_time && ' | '}
                    {spot.leave_time && `Leave: ${spot.leave_time}`}
                  </p>
                )}
                {spot.required_gear && spot.required_gear.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {spot.required_gear.map((gear, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {gear}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Assigned Photographers */}
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">
                    Assigned ({spot.assigned_photographers?.length || 0}/{spot.max_photographers}):
                  </div>
                  {spot.assigned_photographers && spot.assigned_photographers.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {spot.assigned_photographers.map(photoId => {
                        const photographer = getPhotographerById(photoId);
                        return photographer ? (
                          <div key={photoId} className="flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded text-xs">
                            <span>{photographer.name}</span>
                            <button
                              onClick={() => handleUnassignPhotographer(spot.id, photoId)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <UserMinus className="h-3 w-3" />
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No photographers assigned</p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-1 ml-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedSpotForAssign(spot);
                    setAssignDialogOpen(true);
                  }}
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEditSpot(spot)}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteSpot(spot.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
        ) : (
          /* Grid/Thumbnail View */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredSpots.map(spot => (
              <div key={spot.id} className="border rounded-lg overflow-hidden bg-white/50 backdrop-blur-sm hover:shadow-lg transition-shadow">
                {/* Map Thumbnail - Show location on map or placeholder */}
                <div className="relative h-[250px] bg-gradient-to-br from-emerald-100 to-blue-100 flex items-center justify-center">
                  {spot.lat && spot.lng ? (
                    <>
                      <img
                        src={`https://maps.googleapis.com/maps/api/staticmap?center=${spot.lat},${spot.lng}&zoom=18&size=400x400&markers=color:red%7C${spot.lat},${spot.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}`}
                        alt={`Map of ${spot.name}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to OpenStreetMap if Google Maps fails
                          e.target.onerror = null;
                          e.target.src = `https://www.openstreetmap.org/export/embed.html?bbox=${spot.lng-0.005},${spot.lat-0.005},${spot.lng+0.005},${spot.lat+0.005}&layer=mapnik&marker=${spot.lat},${spot.lng}`;
                        }}
                      />
                      <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium">
                        📍 {spot.lat.toFixed(4)}, {spot.lng.toFixed(4)}
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-gray-400">
                      <MapPin className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p className="text-xs">No Location Set</p>
                    </div>
                  )}
                  {/* Role Badge */}
                  <div className="absolute top-2 right-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded shadow-md">
                    {spot.coverage_role}
                  </div>
                  {spot.has_flash && (
                    <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded shadow-md">
                      Flash
                    </div>
                  )}
                </div>
                
                {/* Spot Info */}
                <div className="p-3">
                  <h4 className="font-medium text-sm mb-1 truncate">{spot.name}</h4>
                  {spot.address && (
                    <p className="text-xs text-gray-500 mb-2 truncate">📍 {spot.address}</p>
                  )}
                  
                  {/* Modalities */}
                  {spot.modalities && spot.modalities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {spot.modalities.slice(0, 2).map((modality, idx) => (
                        <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {modality}
                        </span>
                      ))}
                      {spot.modalities.length > 2 && (
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                          +{spot.modalities.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Times */}
                  {(spot.arrive_time || spot.leave_time) && (
                    <p className="text-xs text-gray-500 mb-2 truncate">
                      {spot.arrive_time && `${spot.arrive_time}`}
                      {spot.arrive_time && spot.leave_time && ' - '}
                      {spot.leave_time && `${spot.leave_time}`}
                    </p>
                  )}
                  
                  {/* Assigned Photographers */}
                  <div className="mb-2">
                    <div className="text-xs text-gray-500">
                      Assigned: {spot.assigned_photographers?.length || 0}/{spot.max_photographers}
                    </div>
                    {spot.assigned_photographers && spot.assigned_photographers.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {spot.assigned_photographers.slice(0, 2).map(photoId => {
                          const photographer = getPhotographerById(photoId);
                          return photographer ? (
                            <span key={photoId} className="text-xs bg-emerald-50 px-2 py-0.5 rounded truncate">
                              {photographer.name.split(' ')[0]}
                            </span>
                          ) : null;
                        })}
                        {spot.assigned_photographers.length > 2 && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            +{spot.assigned_photographers.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedSpotForAssign(spot);
                        setAssignDialogOpen(true);
                      }}
                      className="flex-1 text-xs h-7"
                    >
                      <UserPlus className="h-3 w-3 mr-1" />
                      Assign
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingSpot(spot);
                        setSpotForm(spot);
                        setOpen(true);
                      }}
                      className="flex-1 text-xs h-7"
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteSpot(spot.id)}
                      className="text-xs h-7 px-2"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Spot Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSpot ? 'Edit Spot' : 'Create New Spot'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Spot Name</Label>
              <Input
                value={spotForm.name}
                onChange={(e) => setSpotForm({ ...spotForm, name: e.target.value })}
                placeholder="e.g., SKIERG (auto-fills from activity)"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-fills from selected activity, but you can edit it</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={spotForm.lat || ''}
                  onChange={(e) => setSpotForm({ ...spotForm, lat: parseFloat(e.target.value) })}
                  placeholder="Click on map"
                />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={spotForm.lng || ''}
                  onChange={(e) => setSpotForm({ ...spotForm, lng: parseFloat(e.target.value) })}
                  placeholder="Click on map"
                />
              </div>
            </div>

            <div>
              <Label>Address/Location</Label>
              <Input
                value={spotForm.address}
                onChange={(e) => setSpotForm({ ...spotForm, address: e.target.value })}
                placeholder="e.g., Main Arena Floor, Section A"
              />
              <p className="text-xs text-gray-500 mt-1">Enter location description or floor plan reference</p>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={spotForm.description}
                onChange={(e) => setSpotForm({ ...spotForm, description: e.target.value })}
                placeholder="Brief description of the spot"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Coverage Role</Label>
                <Select
                  value={spotForm.coverage_role}
                  onValueChange={(value) => setSpotForm({ ...spotForm, coverage_role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {coverageRoles.map(role => (
                      <SelectItem key={role} value={role}>{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Priority</Label>
                <Select
                  value={spotForm.priority}
                  onValueChange={(value) => setSpotForm({ ...spotForm, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(priority => (
                      <SelectItem key={priority} value={priority}>
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Scene Types / Activities (Select all that apply)</Label>
              <div className="mt-2 space-y-3">
                {Object.entries(modalityGroups).map(([group, modalities]) => (
                  modalities.length > 0 && (
                    <div key={group}>
                      <p className="text-xs font-semibold text-gray-600 mb-2">{group}</p>
                      <div className="flex flex-wrap gap-2">
                        {modalities.map(modality => (
                          <button
                            key={modality}
                            type="button"
                            onClick={() => toggleModality(modality)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                              spotForm.modalities.includes(modality)
                                ? 'bg-emerald-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {modality}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                ))}
                
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2">Custom Activity</p>
                  <div className="flex gap-2">
                    <Input
                      value={customModality}
                      onChange={(e) => setCustomModality(e.target.value)}
                      placeholder="Add custom station/activity"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomModality())}
                    />
                    <Button type="button" onClick={addCustomModality} size="sm" variant="outline">
                      + Add
                    </Button>
                  </div>
                </div>

                {spotForm.modalities.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <span className="text-sm text-gray-600">Selected:</span>
                    {spotForm.modalities.map((modality, idx) => (
                      <span key={idx} className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-sm flex items-center gap-1">
                        {modality}
                        <button onClick={() => removeModality(modality)} className="text-emerald-900 hover:text-red-600">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Required Gear</Label>
              
              {/* Suggested gear based on modality */}
              {spotForm.modalities.length > 0 && getSuggestedGear().length > 0 && (
                <div className="mb-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-purple-900 mb-2">💡 Suggested Gear for {spotForm.modalities.join(', ')}</p>
                  <div className="flex flex-wrap gap-2">
                    {getSuggestedGear().map((gear, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          if (!spotForm.required_gear.includes(gear)) {
                            setSpotForm(prev => ({ ...prev, required_gear: [...prev.required_gear, gear] }));
                          }
                        }}
                        className={`text-xs px-2 py-1 rounded transition ${
                          spotForm.required_gear.includes(gear)
                            ? 'bg-emerald-500 text-white cursor-default'
                            : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-100'
                        }`}
                        disabled={spotForm.required_gear.includes(gear)}
                      >
                        {gear} {spotForm.required_gear.includes(gear) && '✓'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 mb-2">
                <Input
                  value={gearInput}
                  onChange={(e) => setGearInput(e.target.value)}
                  placeholder="e.g., Telephoto Lens"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddGear()}
                />
                <Button onClick={handleAddGear} size="sm">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {spotForm.required_gear.map((gear, idx) => (
                  <span key={idx} className="bg-gray-100 px-2 py-1 rounded text-sm flex items-center gap-1">
                    {gear}
                    <button onClick={() => handleRemoveGear(idx)} className="text-red-500">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={spotForm.has_flash}
                onCheckedChange={(checked) => setSpotForm({ ...spotForm, has_flash: checked })}
              />
              <Label>Flash Required</Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Photographers</Label>
                <Input
                  type="number"
                  min="1"
                  value={spotForm.max_photographers}
                  onChange={(e) => setSpotForm({ ...spotForm, max_photographers: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Arrive Time</Label>
                <Input
                  type="time"
                  value={spotForm.arrive_time}
                  onChange={(e) => setSpotForm({ ...spotForm, arrive_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Leave Time</Label>
                <Input
                  type="time"
                  value={spotForm.leave_time}
                  onChange={(e) => setSpotForm({ ...spotForm, leave_time: e.target.value })}
                />
              </div>
            </div>

            {/* Timing suggestions based on modality */}
            {spotForm.modalities.length > 0 && getTimingSuggestions().length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-900 mb-2">⏰ Timing Suggestions for {spotForm.modalities.join(', ')}</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {getTimingSuggestions().map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="w-full text-left text-xs bg-white border border-blue-200 rounded px-2 py-1.5"
                    >
                      <div className="font-medium text-blue-700 mb-1">{suggestion.modality}</div>
                      <div className="text-blue-900 text-xs">{suggestion.timeInfo}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Click a time to set as arrival time</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>First Athlete Time</Label>
                <Input
                  type="time"
                  value={spotForm.first_athlete_time}
                  onChange={(e) => setSpotForm({ ...spotForm, first_athlete_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Last Athlete Time</Label>
                <Input
                  type="time"
                  value={spotForm.last_athlete_time}
                  onChange={(e) => setSpotForm({ ...spotForm, last_athlete_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Cutoff Time</Label>
                <Input
                  type="time"
                  value={spotForm.cutoff_time}
                  onChange={(e) => setSpotForm({ ...spotForm, cutoff_time: e.target.value })}
                />
              </div>
            </div>

            {/* Break Times */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Break Time 1</Label>
                <Input
                  type="text"
                  placeholder="e.g., 10:00-10:15"
                  value={spotForm.break_time_1 || ''}
                  onChange={(e) => setSpotForm({ ...spotForm, break_time_1: e.target.value })}
                />
              </div>
              <div>
                <Label>Break Time 2</Label>
                <Input
                  type="text"
                  placeholder="e.g., 13:00-13:15"
                  value={spotForm.break_time_2 || ''}
                  onChange={(e) => setSpotForm({ ...spotForm, break_time_2: e.target.value })}
                />
              </div>
            </div>

            {/* Lighting Options */}
            <div className="border-t pt-4">
              <Label className="mb-2 block">💡 Lighting Options</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={spotForm.has_flash || false}
                    onChange={(e) => setSpotForm({ ...spotForm, has_flash: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Flash Required</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={spotForm.use_led_flash || false}
                    onChange={(e) => setSpotForm({ ...spotForm, use_led_flash: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Use LED/Continuous Light (instead of traditional flash)</span>
                </label>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                value={spotForm.notes}
                onChange={(e) => setSpotForm({ ...spotForm, notes: e.target.value })}
                placeholder="Additional notes"
              />
            </div>

            {/* Break coverage — same editor used by the Map → Edit dialog */}
            {editingSpot && (
              <BreakCoverageEditor
                spot={editingSpot}
                eventPhotographers={breakCovPhotographers}
                onChange={(updated) => setEditingSpot(updated)}
                onPersist={onSpotsChange}
              />
            )}

            {/* High-res photos uploaded by photographers from the share link */}
            {editingSpot && (
              <SpotPhotosGallery eventId={eventId} spotId={editingSpot.id} />
            )}

            {/* Reference Images Section */}
            {editingSpot && (
              <div className="border-t pt-4">
                <Label className="mb-2 block">🖼️ Reference Images</Label>
                
                {/* Existing Images */}
                {spotForm.reference_images && spotForm.reference_images.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {spotForm.reference_images.map((imgUrl, idx) => (
                      <div key={idx} className="relative group border rounded overflow-hidden bg-gray-100">
                        <img 
                          src={`${BACKEND_URL}${imgUrl}`} 
                          alt={`Reference ${idx + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() => {
                              const fullUrl = `${window.location.origin}${API}${imgUrl}`;
                              navigator.clipboard.writeText(fullUrl);
                              toast.success('📋 Image URL copied!');
                            }}
                            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                          >
                            Copy URL
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (window.confirm('Delete this image?')) {
                                try {
                                  await axios.delete(
                                    `${API}/spots/${editingSpot.id}/images/${idx}`,
                                    config
                                  );
                                  const newImages = [...spotForm.reference_images];
                                  newImages.splice(idx, 1);
                                  setSpotForm({...spotForm, reference_images: newImages});
                                  toast.success('Image deleted');
                                } catch (error) {
                                  console.error('Error deleting image:', error);
                                  toast.error('Failed to delete image');
                                }
                              }
                            }}
                            className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload New Image */}
                <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const response = await axios.post(
                            `${API}/spots/${editingSpot.id}/upload-image`,
                            formData,
                            { 
                              headers: { 
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data'
                              } 
                            }
                          );
                          
                          const newImages = [...(spotForm.reference_images || []), response.data.image_url];
                          setSpotForm({...spotForm, reference_images: newImages});
                          toast.success('📸 Image uploaded successfully!');
                          e.target.value = ''; // Reset input
                        } catch (error) {
                          console.error('Error uploading image:', error);
                          toast.error('Failed to upload image');
                        }
                      }
                    }}
                    className="hidden"
                    id="spot-ref-image-upload"
                  />
                  <label 
                    htmlFor="spot-ref-image-upload" 
                    className="cursor-pointer text-sm text-gray-600 hover:text-gray-800"
                  >
                    <div className="flex flex-col items-center">
                      <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="font-medium">Click to upload image</span>
                      <span className="text-xs text-gray-500 mt-1">PNG, JPG, GIF, WebP</span>
                    </div>
                  </label>
                </div>
                
                {/* Select from Library Button */}
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setShowLibrarySelector(true)}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Select from Image Library
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setOpen(false);
                setMapMode('normal');
              }}>
                Cancel
              </Button>
              <Button onClick={handleSaveSpot}>
                {editingSpot ? 'Update Spot' : 'Create Spot'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Photographer Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Photographer to {selectedSpotForAssign?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {photographers.map(photographer => {
              const isAssigned = selectedSpotForAssign?.assigned_photographers?.includes(photographer.id);
              return (
                <div
                  key={photographer.id}
                  className={`p-3 border rounded-lg cursor-pointer hover:bg-emerald-50 transition ${
                    isAssigned ? 'bg-emerald-100 border-emerald-300' : ''
                  }`}
                  onClick={() => !isAssigned && handleAssignPhotographer(selectedSpotForAssign.id, photographer.id)}
                >
                  <div className="font-medium">{photographer.name}</div>
                  <div className="text-sm text-gray-600">{photographer.email}</div>
                  {photographer.personal_gear && photographer.personal_gear.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      Gear: {photographer.personal_gear.map(g => g.name).join(', ')}
                    </div>
                  )}
                  {isAssigned && (
                    <div className="text-xs text-emerald-600 mt-1">✓ Already assigned</div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Spot Import Dialog */}
      <SpotImport
        eventId={eventId}
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImportComplete={onSpotsChange}
      />

      
      {/* Image Library Selector Dialog */}
      <Dialog open={showLibrarySelector} onOpenChange={setShowLibrarySelector}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📚 Select Images from Library</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Search and Filter */}
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="🔍 Search images..."
                  value={librarySearchTerm}
                  onChange={(e) => setLibrarySearchTerm(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <select
                value={selectedLibraryPack}
                onChange={(e) => setSelectedLibraryPack(e.target.value)}
                className="border rounded px-3 py-2"
              >
                <option value="All">All Packs</option>
                {libraryPacks.map(pack => (
                  <option key={pack.id} value={pack.name}>
                    {pack.name} ({pack.image_count || 0})
                  </option>
                ))}
              </select>
            </div>

            {/* Selected Count */}
            {selectedLibraryImages.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded px-4 py-2 text-sm">
                ✅ {selectedLibraryImages.length} image(s) selected
              </div>
            )}

            {/* Images Grid */}
            <div className="grid grid-cols-4 gap-3">
              {libraryImages
                .filter(img => {
                  const matchesPack = selectedLibraryPack === 'All' || img.pack === selectedLibraryPack;
                  const matchesSearch = !librarySearchTerm || 
                    img.name.toLowerCase().includes(librarySearchTerm.toLowerCase()) ||
                    img.pack.toLowerCase().includes(librarySearchTerm.toLowerCase());
                  return matchesPack && matchesSearch;
                })
                .map(image => {
                  const isSelected = selectedLibraryImages.includes(image.id);
                  return (
                    <div
                      key={image.id}
                      onClick={() => toggleLibraryImageSelection(image.id)}
                      className={`relative cursor-pointer border-2 rounded overflow-hidden transition-all ${
                        isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={`${BACKEND_URL}${image.image_url}`}
                        alt={image.name}
                        className="w-full h-32 object-cover"
                      />
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                          ✓
                        </div>
                      )}
                      <div className="p-2 bg-white">
                        <p className="text-xs font-medium truncate">{image.name}</p>
                        <p className="text-xs text-gray-500">{image.pack}</p>
                      </div>
                    </div>
                  );
                })}
            </div>

            {libraryImages.filter(img => {
              const matchesPack = selectedLibraryPack === 'All' || img.pack === selectedLibraryPack;
              const matchesSearch = !librarySearchTerm || 
                img.name.toLowerCase().includes(librarySearchTerm.toLowerCase()) ||
                img.pack.toLowerCase().includes(librarySearchTerm.toLowerCase());
              return matchesPack && matchesSearch;
            }).length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No images found. Try adjusting your filters.
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowLibrarySelector(false);
                  setSelectedLibraryImages([]);


      {/* AI Loading Modal */}
      <Dialog open={loadingAI} onOpenChange={() => {}}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8">
            <div className="mb-6">
              <div className="inline-block">
                <svg className="animate-spin h-16 w-16 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              AI Assignment in Progress
            </h3>
            
            <p className="text-gray-600 mb-6">
              {aiLoadingStatus || 'Processing...'}
            </p>
            
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '70%'}}></div>
            </div>
            
            <p className="text-sm text-gray-500">
              This may take 10-30 seconds depending on event size
            </p>
            
            <div className="mt-6 space-y-2 text-xs text-left bg-gray-50 rounded p-3">
              <p className="flex items-center gap-2">
                <span className="text-blue-600">●</span> Analyzing {spots.length} spots
              </p>
              <p className="flex items-center gap-2">
                <span className="text-green-600">●</span> Evaluating {photographers.length} photographers
              </p>
              <p className="flex items-center gap-2">
                <span className="text-purple-600">●</span> Using GPT-4o for intelligent matching
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddLibraryImages}
                disabled={selectedLibraryImages.length === 0}
              >
                Add {selectedLibraryImages.length > 0 ? `(${selectedLibraryImages.length})` : ''} to Spot
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* AI Assignment Preview Dialog */}
      <Dialog open={showAIPreview} onOpenChange={setShowAIPreview}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden z-[9999]">
          <DialogHeader className="border-b pb-3">
            <DialogTitle className="text-xl">🤖 AI Assignment Manager - Drag & Edit Assignments</DialogTitle>
            <p className="text-sm text-gray-600">Review, edit, and manage photographer assignments before applying</p>
          </DialogHeader>
          
          <div className="flex gap-4 h-full overflow-hidden">
            {/* Left Panel - Summary & Warnings */}
            <div className="w-80 flex-shrink-0 space-y-4 overflow-y-auto">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-blue-900">Assignments</p>
                  <p className="text-xl font-bold text-blue-700">{aiAssignments.length}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-green-900">Photographers</p>
                  <p className="text-xl font-bold text-green-700">
                    {new Set(aiAssignments.map(a => a.photographer_id)).size}
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-yellow-900">Warnings</p>
                  <p className="text-xl font-bold text-yellow-700">{aiWarnings.length}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-900">Unassigned</p>
                  <p className="text-xl font-bold text-red-700">{aiUnassigned.length}</p>
                </div>
              </div>

            {/* Warnings */}
            {aiWarnings.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Warnings & Issues:</h3>
                <ul className="space-y-1">
                  {aiWarnings.map((warning, idx) => (
                    <li key={idx} className="text-sm text-yellow-800">• {warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Photographer Schedules with 6-hour highlights */}
            {Object.keys(aiSchedules).length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">👥 Photographer Schedules & Break Times:</h3>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(aiSchedules).map(([photogId, schedule]) => {
                    const photographer = photographers.find(p => p.id === photogId);
                    const needsBreak = schedule.total_hours > 6;
                    
                    return (
                      <div 
                        key={photogId} 
                        className={`border rounded-lg p-4 ${needsBreak ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-200'}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium">{photographer?.name || photogId}</h4>
                          <span className={`text-sm font-semibold ${needsBreak ? 'text-orange-700' : 'text-green-700'}`}>
                            {schedule.total_hours}h
                          </span>
                        </div>
                        
                        {needsBreak && (
                          <div className="bg-orange-100 border border-orange-200 rounded p-2 mb-2">
                            <p className="text-xs font-semibold text-orange-900">
                              🕐 BREAK REQUIRED (>6 hours)
                            </p>
                            <p className="text-xs text-orange-800">
                              Suggested: {schedule.suggested_break}
                            </p>
                            {schedule.break_coverage_needed && schedule.break_coverage_needed.length > 0 && (
                              <p className="text-xs text-orange-800 mt-1">
                                Coverage needed for: {schedule.break_coverage_needed.join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                        
                        <div className="space-y-1">
                          {schedule.assignments_timeline && schedule.assignments_timeline.map((timeline, idx) => (
                            <p key={idx} className="text-xs text-gray-700">• {timeline}</p>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Unassigned Spots */}
            {aiUnassigned.length > 0 && (
              <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                <h3 className="text-xs font-semibold text-red-900 mb-2">❌ Unassigned:</h3>
                <div className="space-y-1">
                  {aiUnassigned.map((spotId, idx) => {
                    const spot = spots.find(s => s.id === spotId);
                    return (
                      <div key={idx} className="text-xs bg-white border border-red-200 rounded px-2 py-1 text-red-800">
                        {spot?.name || spotId}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </div>

            {/* Right Panel - Assignment Details */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <h3 className="font-semibold mb-3 text-lg">📋 Assignment Management - Edit & Reassign</h3>

            {/* Assignment Details - Editable */}
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-3">
                {aiAssignments.map((assignment, idx) => {
                  const spot = spots.find(s => s.id === assignment.spot_id);
                  const photographer = photographers.find(p => p.id === assignment.photographer_id);
                  
                  return (
                    <div key={idx} className="border rounded-lg p-4 bg-white hover:bg-gray-50">
                      {/* Header with Spot and Photographer */}
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-medium text-gray-900">
                              📍 {spot?.name || assignment.spot_id}
                            </p>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {(assignment.confidence * 100).toFixed(0)}% confidence
                            </span>
                          </div>
                          
                          {/* Photographer Selector */}
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Assign to:</span>
                            <select
                              value={assignment.photographer_id}
                              onChange={(e) => handleEditAssignment(idx, 'photographer_id', e.target.value)}
                              className="text-sm border rounded px-2 py-1 bg-white"
                            >
                              {photographers.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleRemoveAssignment(idx)}
                          className="text-red-600 hover:text-red-700"
                        >
                          ✕ Remove
                        </Button>
                      </div>
                      
                      {/* Time Editing Section */}
                      <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-2">
                        <p className="text-xs font-semibold text-gray-700 mb-2">⏰ Timing (Edit if needed):</p>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-gray-600">Arrive Time</label>
                            <input
                              type="time"
                              value={spot?.arrive_time || ''}
                              onChange={(e) => {
                                // Update spot time via API would go here
                                console.log('Update arrive time:', e.target.value);
                              }}
                              className="w-full text-xs border rounded px-2 py-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Leave Time</label>
                            <input
                              type="time"
                              value={spot?.leave_time || ''}
                              onChange={(e) => {
                                console.log('Update leave time:', e.target.value);
                              }}
                              className="w-full text-xs border rounded px-2 py-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">First Athlete</label>
                            <input
                              type="time"
                              value={spot?.first_athlete_time || ''}
                              onChange={(e) => {
                                console.log('Update first athlete time:', e.target.value);
                              }}
                              className="w-full text-xs border rounded px-2 py-1"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* AI Reasoning */}
                      <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2">
                        <p className="text-xs font-semibold text-blue-900">💡 AI Reasoning:</p>
                        <p className="text-xs text-blue-800">{assignment.reasoning}</p>
                      </div>
                      
                      {/* Timing Notes */}
                      {assignment.timing_notes && (
                        <p className="text-xs text-gray-600 mb-1">⏰ {assignment.timing_notes}</p>
                      )}
                      
                      {/* Gear Match */}
                      {assignment.gear_match && assignment.gear_match.length > 0 && (
                        <p className="text-xs text-green-700 mb-1">
                          ✓ Gear Match: {assignment.gear_match.join(', ')}
                        </p>
                      )}
                      
                      {/* Potential Issues */}
                      {assignment.potential_issues && assignment.potential_issues.length > 0 && (
                        <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2">
                          <p className="text-xs font-semibold text-yellow-900">⚠️ Potential Issues:</p>
                          {assignment.potential_issues.map((issue, i) => (
                            <p key={i} className="text-xs text-yellow-800">• {issue}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t mt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowAIPreview(false)}
              >
                Cancel
              </Button>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    // Re-run AI analysis
                    setShowAIPreview(false);
                    handleAIAssign();
                  }}
                >
                  🔄 Regenerate
                </Button>
                <Button onClick={handleApplyAIAssignments}>
                  ✅ Apply {aiAssignments.length} Assignments
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>


    </div>
  );
}
